// src/scenes/zbalance/ViewZBalance.jsx
import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import FilterZbalance from "./FilterZbalance";

import {
  Box,
  
  useTheme,
} from "@mui/material";

import { tokens } from "../../theme";

/* ---------- helpers ---------- */
const fmt = (date) => (date ? date : "-");


/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}


export default function ViewZBalance() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  /* ✅ ROLE */
  const lsUser = JSON.parse(localStorage.getItem("user") || "{}");

  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || ""
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw
    );

  const currentUserId = String(lsUser?.id ?? lsUser?.userId ?? "");

  /* ✅ FILTER STATE */
  const [filters, setFilters] = React.useState({
    userId: "",
    fromDate: "",
    toDate: "",
  });

  const [users, setUsers] = React.useState([]);

  /* ✅ LOAD USERS (ADMIN ONLY) */
  React.useEffect(() => {
    if (isSalesMan) return;

    getJSON("/api/user").then(({ data }) => {
      const salesUsers = Array.isArray(data)
        ? data.filter(
            (u) => (u.Role?.name || "").toLowerCase() === "sales"
          )
        : [];

      setUsers(
        salesUsers.map((u) => ({ id: u.id, name: u.fullName }))
      );
    });
  }, [isSalesMan]);

  /* ---------- STATE ---------- */
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  /* ✅ BUILD FILTER API */
  const buildFilterUrl = () => {
    let url = isSalesMan
      ? `/api/zbalance/user/${currentUserId}`
      : `/api/zbalance`;

    const params = new URLSearchParams();

    if (filters.fromDate && filters.toDate) {
      params.append("startDate", filters.fromDate);
      params.append("endDate", filters.toDate);
    } else if (filters.fromDate) {
      params.append("date", filters.fromDate);
    }

    if (!isSalesMan && filters.userId) {
      params.append("userId", filters.userId);
    }

    const query = params.toString();
    if (query) url += `?${query}`;

    return url;
  };

  /* ✅ LOAD DATA */
  const load = React.useCallback(async () => {
    setLoading(true);

    const apiUrl = buildFilterUrl();
    const { data } = await getJSON(apiUrl);

    setLoading(false);

    setRows(
      (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        userName: r?.User?.fullName || "—",
      }))
    );
  }, [filters, isSalesMan, currentUserId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleReset = () => {
    setFilters({ userId: "", fromDate: "", toDate: "" });
    setTimeout(load, 0);
  };

  /* ✅ TABLE */
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "userName", headerName: "User", flex: 1 },
    { field: "date", headerName: "Date", width: 140, valueGetter: (p) => fmt(p.row.date) },
    { field: "zamount", headerName: "Z Amount", width: 140 },
    { field: "bankdeposit", headerName: "Bank Deposit", width: 140 },
    { field: "totalsales", headerName: "Total Sales", width: 140 },
  ];

 // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };


  return (
    <>
      <FilterZbalance
        isSalesMan={isSalesMan}
        users={users}
        filters={filters}
        setFilters={setFilters}
        onFilter={load}
        onReset={handleReset}
      />

      <Box m="20px">
        <Header title="Z BALANCES" subtitle="View Z Balance Entries" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>
    </>
  );
}
